import * as React from "react"
import { Head, usePage } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import PublicLayout from "@/components/PublicLayout"
import IncludedTiles from "@/components/IncludedTiles"
import { HeroFilm } from "@/components/gisela/HeroBand"
import { bandBg } from "@/components/gisela/TextBand"
import {
  BuildingBand,
  GettingHereBand,
  LocationHero,
  RoomBand,
  VideoTourBand,
  fill,
  type HeroSlides,
  type Photo,
  type SiteRoom,
  type TourVideo,
} from "@/components/location/bands"
import { presentBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import type { GiselaTherapist, PageMeta, PageProps } from "@/types/inertia"
import { BrandMotif } from "@/components/ui/brand-motif"

interface SiteDetail {
  id: number
  name: string
  slug: string
  region: string
  address: string
  address_lines: string[]
  maps_url: string | null
  /** Map pin. Null until an admin fills them in on the location's own screen. */
  latitude: number | null
  longitude: number | null
  opening_hours: string | null
  features: string[]
  description: string | null
  href: string
  gallery_href: string
  rooms: SiteRoom[]
}

interface FeatureItem {
  icon: string
  title_en: string
  desc_en?: string
}

interface Props {
  site: SiteDetail
  /** The gallery's non-room photos — outside, reception, kitchen. */
  building: { photos: Photo[] }
  /** The newest video filed in this location's media section, if any. */
  video?: TourVideo | null
  /** Gisela, for the therapy band — nil until a therapist record exists. */
  therapist?: GiselaTherapist | null
  /** The hero's background photos, already cut to its display settings. */
  hero?: HeroSlides
  content: {
    hero: { heading?: string; subheading?: string; _hidden?: boolean }
    whats_included: { items?: FeatureItem[]; _hidden?: boolean }
    gisela_callout: { heading?: string; body?: string; cta?: string; photo?: Photo | null; _hidden?: boolean }
    building: { heading?: string; body?: string; _hidden?: boolean }
    /** This location's rooms from the Rent a Room rate card, with the band's own words around them. */
    rates: { heading?: string; body?: string; table?: string | null; note?: string; _hidden?: boolean }
    location: { directions?: string; photo?: Photo; _hidden?: boolean }
  }
  /** Registry entry ids in display order — the admin's arrangement. */
  section_order?: string[]
  meta?: PageMeta
}

const BOOK_A_VIEWING = "/rent-a-room#book-a-viewing"

/**
 * A location's own page — the same page for every venue, fed with that site's
 * rooms, photos and sections: the hero, one band per room beside its own
 * gallery photos, the building strip, what's included, the Therapy with
 * Gisela hero band, the video tour when one is filed, location & getting
 * here, and the way to the viewing form on Rent a Room.
 */
export default function Location({
  site,
  building,
  video,
  therapist,
  hero = { slides: [], interval_seconds: 6 },
  content,
  section_order,
  meta,
}: Props) {
  const { brand_urls, translations } = usePage<PageProps>().props
  const sanctuary_locations = usePage<PageProps>().props.sanctuary_locations ?? []
  const t = translations.sanctuary_pages.location
  const tc = translations.sanctuary_pages.common
  const K = site.slug
  const GALLERY = site.gallery_href
  const title = meta?.title ?? fill(t.meta_title, { name: site.name })
  const description = meta?.description ?? fill(t.meta_description, { name: site.name })
  // "Prestwich and Hebden Bridge" — every location, so a new one changes the
  // sentence without a deploy.
  const locationNames = (sanctuary_locations.length > 0 ? sanctuary_locations.map((l) => l.label) : [site.name])
    .join(t.names_joiner)

  // Every band below the hero, named by its registry entry so the admin's
  // order applies; the cream/stone colours alternate over the ones shown.
  const bands = presentBands(
    [
      {
        id: `${K}-rooms`,
        present: true,
        render: (i) => (
                <section id="rooms" className={`${bandBg(i)} pt-16`}>
                  <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <h2>{t.rooms_heading}</h2>
                    <p className="mt-3 text-ink-muted">{t.rooms_sub}</p>
                  </div>

                  {/* One band per room, the room's own photos beside its
                      description — alternating sides, a hairline between. The
                      photos are the room's gallery section; the whole of it is
                      one click away. */}
                  {site.rooms.map((room, index) => (
                    <RoomBand
                      key={room.id}
                      room={room}
                      gallery={GALLERY}
                      flip={index % 2 === 1}
                      first={index === 0}
                      extra={
                        <a href={BOOK_A_VIEWING} className="text-sm text-ink-muted hover:text-ink-body">
                          {t.book_viewing_plain}
                        </a>
                      }
                    />
                  ))}
                </section>
        ),
      },
      {
        id: `${K}-video-tour`,
        present: !!video && !!(video.embed_src || video.file_url),
        render: (i) => <VideoTourBand video={video!} heading={fill(t.tour_heading, { name: site.name })} className={bandBg(i)} />,
      },
      {
        id: `${K}-building`,
        present: !sectionHidden(content.building) && !!content.building.heading,
        render: (i) => (
          <BuildingBand
            heading={content.building.heading!}
            body={content.building.body}
            photos={building.photos}
            gallery={GALLERY}
            galleryLabel={fill(t.full_gallery, { name: site.name })}
            className={bandBg(i)}
          />
        ),
      },
      {
        id: `${K}-whats-included`,
        present: !sectionHidden(content.whats_included) && (content.whats_included.items ?? []).length > 0,
        render: (i) => (
                  <section id="whats-included" className={`relative overflow-hidden ${bandBg(i)} py-16`}>
                    <BrandMotif variant="sprig" className="absolute -bottom-12 -right-8" />
                    <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
                      <h2 className="text-center">{tc.whats_included}</h2>
                      <div className="mt-10">
                        <IncludedTiles items={content.whats_included.items ?? []} />
                      </div>
                    </div>
                  </section>
        ),
      },
      {
        id: `${K}-rates`,
        // The rates come from Rent a Room's card; no rooms cut from it for
        // this location and no words of the band's own means no band.
        present: !sectionHidden(content.rates) && !!(content.rates.table || content.rates.body),
        render: (i) => (
                  <section id="rates" className={`${bandBg(i)} py-16`}>
                    <div className="mx-auto max-w-3xl px-6 lg:px-8">
                      <h2>{content.rates.heading || fill(t.rates_heading, { name: site.name })}</h2>
                      {content.rates.body && (
                        <div className="mt-5 text-ink-muted [&_p]:mt-4 [&_p:first-child]:mt-0">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.rates.body}</ReactMarkdown>
                        </div>
                      )}
                      {content.rates.table && (
                        <div className="body-content mt-6 overflow-x-auto text-ink-body">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.rates.table}</ReactMarkdown>
                        </div>
                      )}
                      {content.rates.note && (
                        <div className="mt-5 text-sm text-ink-muted [&_p]:mt-3 [&_p:first-child]:mt-0">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.rates.note}</ReactMarkdown>
                        </div>
                      )}
                      <p className="mt-6">
                        <a href="/rent-a-room#rates">{t.rates_link}</a>
                      </p>
                    </div>
                  </section>
        ),
      },
      {
        id: `${K}-gisela-callout`,
        present: !sectionHidden(content.gisela_callout) && !!content.gisela_callout.body,
        render: () => (
                  // The resident-therapist band, a hero like the OCD page's:
                  // the admin's chosen photo under the blue film, or the plain
                  // blue — her portrait and name beside the admin's copy, and
                  // one way through to her site.
                  <section className="relative overflow-hidden bg-teal py-16 text-white lg:py-20">
                    <HeroFilm photo={content.gisela_callout.photo} />
                    <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
                      <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 text-center sm:grid sm:grid-cols-[auto_1fr] sm:items-center sm:gap-8 sm:text-left lg:gap-12">
                        {therapist?.photo_url && (
                          <img
                            src={therapist.photo_url}
                            alt={therapist.name}
                            className="h-36 w-36 rounded-2xl object-cover ring-1 ring-white/25 sm:h-48 sm:w-48 lg:h-56 lg:w-56"
                          />
                        )}
                        <div>
                          {/* No eyebrow or name above it: the heading names her,
                              and so does the first line of the copy below. */}
                          <h2 className="text-white">{content.gisela_callout.heading}</h2>
                          {/* The markdown paragraphs would otherwise take the
                              base ink colour; white on the blue, as the OCD
                              hero's copy is. */}
                          <div className="body-content mt-4 max-w-2xl text-white [&_p]:text-white">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.gisela_callout.body ?? ""}</ReactMarkdown>
                          </div>
                          {/* Her own site, not the venue's redirect to it — see
                              the gisela_therapy shared prop. */}
                          {content.gisela_callout.cta && (
                            <a
                              href={brand_urls.gisela_therapy}
                              className="mt-6 inline-block rounded-full bg-white px-6 py-2.5 text-sm font-medium text-teal no-underline shadow transition-opacity hover:opacity-90"
                            >
                              {content.gisela_callout.cta}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>
        ),
      },
      {
        id: `${K}-location`,
        present: !sectionHidden(content.location),
        render: (i) => (
          <GettingHereBand
            site={site}
            heading={t.location_heading}
            directions={content.location.directions}
            photo={content.location.photo}
            className={bandBg(i)}
          />
        ),
      },
      {
        id: `${K}-viewing-cta`,
        present: true,
        render: (i) => (
                <section className={`relative overflow-hidden ${bandBg(i)} py-16`}>
                  <BrandMotif variant="disc" className="absolute -top-28 -right-36" />
                  <div className="relative mx-auto max-w-3xl px-6 text-center lg:px-8">
                    <h2>{t.interested_heading}</h2>
                    <p className="mt-4 text-ink-muted">
                      {fill(t.interested_body, { name: site.name })}
                    </p>
                    <a
                      href={BOOK_A_VIEWING}
                      className="mt-6 inline-block rounded-full bg-teal px-7 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90"
                    >
                      {tc.book_a_viewing}
                    </a>
                    {/* The way to the room-hire page proper, in words that
                        say what it is — the pill above only names its form. */}
                    <p className="mt-5">
                      <a href="/rent-a-room">{t.room_hire_link}</a>
                    </p>
                  </div>
                </section>
        ),
      },
      {
        id: `${K}-future-note`,
        present: true,
        render: (i) => (
                <section className={`${bandBg(i)} border-t border-hairline py-10`}>
                  <div className="mx-auto max-w-3xl px-6 text-center lg:px-8">
                    <p className="text-sm text-ink-muted">
                      {fill(t.future_note, { names: locationNames })}
                    </p>
                  </div>
                </section>
        ),
      },
    ],
    section_order,
  )

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicLayout>
        {/* ── Hero ───────────────────────────────────────────────────── */}
        {!sectionHidden(content.hero) && (
          <LocationHero
            hero={hero}
            eyebrow={site.region}
            heading={content.hero.heading || `Sanctuary Therapy Centre — ${site.name}`}
            subheading={content.hero.subheading}
            address={site.address}
          >
            {/* The two ways out of this hero, in the order the page serves:
                book a viewing of the rooms — the page's one real ask, with
                the rooms themselves right below — or, for the visitor who
                came looking for therapy rather than a room, go to Gisela.
                One column, both the width of the longer label — stacked
                pills of different widths read as a mistake. */}
            <div className="mx-auto mt-8 flex w-full max-w-sm flex-col gap-3">
              {/* The transparent border keeps the filled pill exactly as tall
                  as the outlined one below it. */}
              <a
                href={BOOK_A_VIEWING}
                className="block w-full rounded-full border border-transparent bg-white px-8 py-3 text-center text-sm font-medium text-teal no-underline shadow transition-opacity hover:opacity-90"
              >
                {tc.book_a_viewing}
              </a>
              <a
                href={brand_urls.gisela_therapy}
                className="block w-full rounded-full border border-white/40 px-8 py-3 text-center text-sm font-medium text-white no-underline transition-colors hover:border-white hover:bg-white/10"
              >
                {t.therapy_with_gisela}
              </a>
            </div>
          </LocationHero>
        )}

        {bands.map((band, i) => (
          <React.Fragment key={band.id}>{band.render(i)}</React.Fragment>
        ))}
      </PublicLayout>
    </>
  )
}
